import { Ellipsis, Ship, UserPen, UserRoundX } from 'lucide-react'
import type { ReactNode } from 'react'

import { useConnectedAccount } from '@/providers/connectedAccount'
import type { ConnectedAccount } from '@/providers/connectedAccount/types'
import { useUserContext } from '@/providers/user/useUserContext'
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '@/ui-components/Dropdown'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/ui-components/Table'

import type { OrganizationUser } from './types'

const concatenateAccounts = (user: OrganizationUser, connectedAccounts: ConnectedAccount[]): ReactNode => {
  if (!connectedAccounts.length || !user.connectedAccounts.length) {
    return '-'
  }

  return (
    <>
      {connectedAccounts
        .filter((account) => user.connectedAccounts.includes(account.id))
        .map((account) => (
          <div
            key={`${account.id}-${user.id}`}
            data-testid={`${account.id}-${user.id}`}
          >
            {account.name}
          </div>
        ))}
    </>
  )
}

type ActiveOrganizationUsersTableProps = {
  users: OrganizationUser[]
  openEditUserModal: (user: OrganizationUser) => void
  openAddUserToConnectedAccountsModal: (user: OrganizationUser) => void
  openRemoveUserFromConnectedAccountsModal: (user: OrganizationUser) => void
  openRemoveUserModal: (user: OrganizationUser) => void
}

export const ActiveOrganizationUsersTable = ({
  users,
  openEditUserModal,
  openAddUserToConnectedAccountsModal,
  openRemoveUserFromConnectedAccountsModal,
  openRemoveUserModal,
}: ActiveOrganizationUsersTableProps) => {
  const { user: currentUser } = useUserContext()
  const { connectedAccounts } = useConnectedAccount()

  return (
    <>
      <Table className="w-[calc(100vw-3rem)] md:w-auto">
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Email</TableHeader>
            <TableHeader>Role</TableHeader>
            <TableHeader>Vessels</TableHeader>
            <TableHeader>Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.map((user: OrganizationUser) => (
            <TableRow key={user.id}>
              <TableCell>{user.name}</TableCell>
              <TableCell>{user.email}</TableCell>
              <TableCell className="capitalize">{user.role}</TableCell>
              <TableCell>{concatenateAccounts(user, connectedAccounts)}</TableCell>
              <TableCell>
                <div className="relative z-10 pointer-events-auto">
                  <Dropdown>
                    <DropdownButton plain>
                      <Ellipsis className="size-4" />
                    </DropdownButton>
                    <DropdownMenu anchor="bottom end">
                      <DropdownItem onClick={() => openEditUserModal(user)}>
                        <UserPen data-slot="icon" />
                        Edit Role
                      </DropdownItem>
                      <DropdownItem onClick={() => openAddUserToConnectedAccountsModal(user)}>
                        <Ship data-slot="icon" />
                        Add to Vessel
                      </DropdownItem>
                      {currentUser?.id !== user.id && (
                        <>
                          <DropdownItem onClick={() => openRemoveUserFromConnectedAccountsModal(user)}>
                            <UserRoundX data-slot="icon" />
                            Remove from Vessel
                          </DropdownItem>
                          <DropdownItem onClick={() => openRemoveUserModal(user)}>
                            <UserRoundX data-slot="icon" />
                            Remove User
                          </DropdownItem>
                        </>
                      )}
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  )
}
